/**
 * TileFigure — the frame every launcher figure shares: the headline number beside its graphic,
 * the graphic's key under it. A small tile has room for one line, so it draws the graphic and
 * a short text after it.
 */

import type { ReactElement, ReactNode } from 'react';
import React from 'react';

import { cn } from '@/lib/utils';
import { LauncherTileSize } from '@/types/panels';

interface Props {
    size: LauncherTileSize;
    /** The headline: what the place holds, as a number. */
    value: string;
    /** What the value counts, under it. */
    unit: string;
    graphic: ReactNode;
    /** Under the graphic: its key, or the ends of its axis. */
    legend?: ReactNode;
    /** The one line of a small tile, after its graphic. */
    brief: string;
}

export const TileFigure = ({
    size, value, unit, graphic, legend, brief,
}: Props): ReactElement => {
    if (size === LauncherTileSize.Small) {
        return (
            <span className="flex min-w-0 items-center gap-2">
                <span className="flex min-w-0 shrink items-center">{graphic}</span>
                <span className="shrink-0 truncate text-xs font-medium text-occ-foreground-main">
                    {brief}
                </span>
            </span>
        );
    }
    const isLarge = size === LauncherTileSize.Large;
    return (
        <span className={cn('flex min-w-0 items-end', isLarge ? 'gap-6' : 'gap-4')}>
            <span className="flex shrink-0 flex-col gap-1.5">
                <span className={cn(
                    'font-semibold leading-none tabular-nums text-occ-foreground-high-contrast',
                    isLarge ? 'text-4xl' : 'text-2xl',
                )}
                >
                    {value}
                </span>
                <span className="text-xs text-occ-foreground-secondary">{unit}</span>
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-2">
                {graphic}
                {legend}
            </span>
        </span>
    );
};
